import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import FullScreenButton from './FullScreenButton';

const CUSTOMER_ID = 'otoki';
const MIN_ROWS = 6;
const HEADERS = ['입고처(공장)명', '주소', '일반전화', '휴대전화', '담당자', '정렬'];

interface Props {
  isFullScreen: boolean;
  setIsFullScreen: (v: boolean) => void;
  embedded?: boolean;
}

const ProcurementDestinationPage: React.FC<Props> = ({ isFullScreen, setIsFullScreen, embedded = false }) => {
  const hotRef = useRef<any>(null);
  const deletedIdsRef = useRef<Set<string>>(new Set());
  const [tableData, setTableData] = useState<any[][]>([]);
  const [destIds, setDestIds] = useState<(string | null)[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const loadDestinations = useCallback(async () => {
    setLoading(true);
    deletedIdsRef.current.clear();
    try {
      const res = await fetch(`/api/procurement/destinations?customerId=${CUSTOMER_ID}`);
      if (!res.ok) throw new Error('입고처(공장) 목록을 불러오지 못했습니다.');
      const data = await res.json();
      const rows = data.map((r: {
        name: string;
        address?: string | null;
        phone_landline?: string | null;
        phone_mobile?: string | null;
        phones?: string | null;
        manager_name?: string | null;
        sort_order?: number;
      }) => {
        let landline = r.phone_landline || '';
        let mobile = r.phone_mobile || '';
        if (!landline && !mobile && r.phones) {
          const parts = String(r.phones)
            .split(/[\n,/;]+/)
            .map(s => s.trim())
            .filter(Boolean);
          if (parts.length >= 2) {
            landline = parts.find(p => !/^010/.test(p)) || parts[0];
            mobile = parts.find(p => /^010/.test(p)) || parts[1];
          } else if (parts.length === 1) {
            if (/^010/.test(parts[0])) mobile = parts[0];
            else landline = parts[0];
          }
        }
        return [
          r.name,
          r.address || '',
          landline,
          mobile,
          r.manager_name || '',
          r.sort_order ?? 0,
        ];
      });
      while (rows.length < MIN_ROWS) rows.push(['', '', '', '', '', 0]);
      setDestIds(data.map((r: { id: string }) => r.id).concat(Array(Math.max(0, MIN_ROWS - data.length)).fill(null)));
      setTableData(rows);
    } catch (e: unknown) {
      setMessage(e instanceof Error ? e.message : '조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDestinations(); }, [loadDestinations]);

  const syncTableData = () => {
    setTableData(hotRef.current?.hotInstance?.getData() || []);
  };

  const handleSave = async () => {
    const hot = hotRef.current?.hotInstance;
    if (!hot) return;
    const data: any[][] = hot.getData();

    const deleteIds = new Set<string>(deletedIdsRef.current);
    const upsertRows: Array<{
      id?: string;
      name: string;
      address: string | null;
      phone_landline: string | null;
      phone_mobile: string | null;
      manager_name: string | null;
      sort_order: number;
    }> = [];

    data.forEach((row, idx) => {
      const id = destIds[idx] || undefined;
      const name = String(row[0] || '').trim();
      const address = String(row[1] || '').trim() || null;
      const phone_landline = String(row[2] || '').trim() || null;
      const phone_mobile = String(row[3] || '').trim() || null;
      const manager_name = String(row[4] || '').trim() || null;
      const sort_order = Number(row[5]) || 0;

      if (id && !name) {
        deleteIds.add(id);
        return;
      }
      if (!name) return;

      upsertRows.push({ id, name, address, phone_landline, phone_mobile, manager_name, sort_order });
    });

    if (upsertRows.length === 0 && deleteIds.size === 0) {
      alert('저장할 변경사항이 없습니다.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/api/procurement/destinations/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: CUSTOMER_ID,
          rows: upsertRows,
          deleteIds: [...deleteIds],
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || '저장 실패');
      deletedIdsRef.current.clear();
      setMessage('저장되었습니다.');
      await loadDestinations();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRow = () => {
    setTableData(prev => [...prev, ['', '', '', '', '', 0]]);
    setDestIds(prev => [...prev, null]);
  };

  const handleRemoveRow = (index: number, amount: number) => {
    setDestIds(prev => {
      const next = [...prev];
      for (let i = 0; i < amount; i++) {
        const id = next[index + i];
        if (id) deletedIdsRef.current.add(id);
      }
      next.splice(index, amount);
      return next;
    });
    syncTableData();
  };

  const handleCreateRow = (index: number, amount: number) => {
    setDestIds(prev => {
      const next = [...prev];
      for (let i = 0; i < amount; i++) {
        next.splice(index, 0, null);
      }
      return next;
    });
    syncTableData();
  };

  return (
    <div style={{
      padding: embedded ? 0 : '12px 16px',
      height: embedded ? '100%' : 'calc(100vh - 48px)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
    }}>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 style={{ margin: 0, color: '#1976d2', fontSize: 22 }}>조달실 — 입고처(공장) 관리</h2>
          <FullScreenButton isFullScreen={isFullScreen} setIsFullScreen={setIsFullScreen} />
        </div>
      )}
      <p style={{ color: '#666', fontSize: 14, margin: '0 0 12px' }}>
        OLS 송장 도착지(공장·창고)를 등록합니다. 입고처(공장)명을 Delete 키로 지우거나 행을 삭제한 뒤 저장하면 삭제됩니다. (해당 입고처(공장) 송장은 함께 삭제됩니다.) 일반전화와 휴대전화를 각각 입력하세요.
      </p>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button type="button" onClick={handleAddRow} style={{ padding: '6px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>+ 행 추가</button>
        <button type="button" onClick={handleSave} disabled={saving || loading} style={{ padding: '6px 16px', background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{saving ? '저장 중…' : '저장'}</button>
        <button type="button" onClick={loadDestinations} style={{ padding: '6px 16px', background: '#eee', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>새로고침</button>
      </div>
      {message && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{message}</div>}
      {loading ? (
        <div style={{ padding: 24, color: '#888' }}>불러오는 중…</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <HotTable
            ref={hotRef}
            data={tableData}
            colHeaders={HEADERS}
            rowHeaders
            width="100%"
            height="100%"
            licenseKey={process.env.REACT_APP_HANDSONTABLE_LICENSE_KEY || 'non-commercial-and-evaluation'}
            stretchH="all"
            contextMenu={{
              items: {
                remove_row: { name: '행 삭제' },
              },
            }}
            afterChange={(changes, source) => {
              if (source === 'loadData' || !changes) return;
              syncTableData();
            }}
            afterRemoveRow={handleRemoveRow}
            afterCreateRow={handleCreateRow}
            columns={[
              { type: 'text' },
              { type: 'text' },
              { type: 'text' },
              { type: 'text' },
              { type: 'text' },
              { type: 'numeric' },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default ProcurementDestinationPage;
